import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Cpu, Heart, Lock, Star, Globe, Sparkles, Activity, Zap } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import TierGrid from "../components/TierGrid";
import FAQ from "../components/FAQ";
import CountUp from "../components/CountUp";

const HERO_BG = "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=1600&q=80";
const BUILDINGS_BG = "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=1600&q=80";
const FARM_BG = "https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?auto=format&fit=crop&w=1600&q=80";
const MONEY_BG = "https://images.unsplash.com/photo-1556740749-887f6717d7e4?auto=format&fit=crop&w=1600&q=80";
const COUPLE_IMG = "https://static.prod-images.emergentagent.com/jobs/a4dc1f8c-4f02-4078-ad8b-aaca1a159173/images/0630e08aa8b1d38ab83d26dc1bf045700511976aa6832aa8dc594bd6ac07e045.png";
const AI_IMG = "https://static.prod-images.emergentagent.com/jobs/a4dc1f8c-4f02-4078-ad8b-aaca1a159173/images/f9eee6383780c91655a8552ee8ebd549d7c8f9117698593386d9f67add092a47.png";

const TESTIMONIALS = [
  { name: "James & Sarah Mitchell", role: "Joint Investors â€¢ USA", text: "We started with the Growth plan and reinvested our returns. Six months in, our joint portfolio is up 124%. The AI bot is a game-changer.", img: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0JTIwc21pbGluZ3xlbnwwfHx8fDE3Nzk1NDU4OTN8MA&ixlib=rb-4.1.0&q=85" },
  { name: "Henrik Berg", role: "Elite Tier â€¢ Norway", text: "Smooth withdrawals, transparent dashboard, and the AI trade feed is mesmerising. Easily the best platform I've used.", img: "https://images.unsplash.com/photo-1611432579402-7037e3e2c1e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0JTIwc21pbGluZ3xlbnwwfHx8fDE3Nzk1NDU4OTN8MA&ixlib=rb-4.1.0&q=85" },
  { name: "Priya Anand", role: "Platinum VIP â€¢ Singapore", text: "The Joint Account feature lets my husband and I invest as one. We finally see our wealth growing together â€” feels intentional.", img: "https://images.unsplash.com/photo-1484863137850-59afcfe05386?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0JTIwc21pbGluZ3xlbnwwfHx8fDE3Nzk1NDU4OTN8MA&ixlib=rb-4.1.0&q=85" },
];

const STEPS = [
  { icon: ShieldCheck, title: "Sign up & verify", desc: "Create your individual or joint account in minutes. Quick identity verification keeps your funds safe." },
  { icon: Sparkles, title: "Activate the AI Bot", desc: "Choose a subscription tier from Starter to Platinum VIP. This unlocks the bot and sets your ROI rate." },
  { icon: Cpu, title: "Fund real investment", desc: "After activation, fund a Growth tier or higher real investment for the AI bot to trade." },
  { icon: Activity, title: "Withdraw on day 30", desc: "Returns are credited at the end of each 30-day cycle. Withdraw to your wallet anytime, 24-48h." },
];

export default function HomePage() {
  return (
    <div className="min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-cover bg-center brightness-90 opacity-50" style={{ backgroundImage: `url(${HERO_BG})` }} />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0E27]/40 via-[#0A0E27]/70 to-[#050814]/95" />
        </div>

        <div className="max-w-7xl mx-auto px-6 pt-20 pb-28 lg:pt-28 lg:pb-36">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-6">
              <span className="w-1.5 h-1.5 bg-[#00D68F] rounded-full blink"></span>
              <span className="text-xs uppercase tracking-widest text-white/80">Now serving 38,400+ investors worldwide</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05]">
              Invest Smarter.<br />
              <span className="gold-grad">Grow Together.</span>
            </h1>
            <p className="text-base sm:text-lg text-white/70 mt-7 max-w-2xl leading-relaxed">
              Duocapital is the premium AI-powered investment platform built for individuals and couples. Activate an AI Bot tier, fund real trading capital, and let the engine work 24/7.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-9">
              <Link to="/register" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="hero-cta-primary">
                Start Investing <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/invest" className="btn-outline inline-flex items-center justify-center gap-2" data-testid="hero-cta-secondary">
                View Plans
              </Link>
            </div>

            {/* Trust strip */}
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-2xl">
              <div>
                <div className="font-display text-2xl sm:text-3xl lg:text-4xl font-black text-[#D4AF37]">
                  $<CountUp value={142.78} decimals={2} />M+
                </div>
                <div className="text-xs text-white/50 uppercase tracking-widest mt-1">Total Managed</div>
              </div>
              <div>
                <div className="font-display text-2xl sm:text-3xl lg:text-4xl font-black text-[#D4AF37]">
                  <CountUp value={38400} />+
                </div>
                <div className="text-xs text-white/50 uppercase tracking-widest mt-1">Active Investors</div>
              </div>
              <div>
                <div className="font-display text-2xl sm:text-3xl lg:text-4xl font-black text-[#00D68F]">
                  <CountUp value={99.4} decimals={1} />%
                </div>
                <div className="text-xs text-white/50 uppercase tracking-widest mt-1">Satisfaction</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* AS FEATURED IN */}
      <section className="border-y border-white/5 bg-black/30 py-8" data-testid="featured-strip">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-center md:justify-between gap-y-4 gap-x-10 text-white/40 font-display">
          <span className="text-xs uppercase tracking-[0.3em] text-white/30">As featured in</span>
          <span className="text-2xl font-black tracking-tight">Bloomberg</span>
          <span className="text-2xl font-black italic tracking-tight">Forbes</span>
          <span className="text-2xl font-black tracking-tight">REUTERS</span>
          <span className="text-2xl font-black tracking-tight">Financial Times</span>
          <span className="text-2xl font-black tracking-tight">CoinDesk</span>
        </div>
      </section>

      {/* TIERS */}
      <section className="max-w-7xl mx-auto px-6 py-24" id="plans" data-testid="plans-section">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12 gap-6">
          <div>
            <div className="label-eyebrow">AI Bot Subscription Tiers</div>
            <h2 className="font-display text-4xl lg:text-5xl font-black mt-3 max-w-xl leading-tight">Choose your <span className="gold-grad">bot advantage</span>.</h2>
          </div>
          <p className="text-white/60 max-w-md text-sm">Five AI Bot subscription tiers unlock trading access, set your ROI rate, and define your loan limit. Real investment funding comes after activation.</p>
        </div>
        <TierGrid />
      </section>

      {/* HOW IT WORKS */}
      <section className="relative bg-black/30 py-24 overflow-hidden" data-testid="how-section">
        <div className="absolute inset-0 -z-10">
          <img src={BUILDINGS_BG} alt="Urban financial buildings" className="w-full h-full object-cover opacity-30 brightness-90" />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="label-eyebrow">How it works</div>
            <h2 className="font-display text-4xl lg:text-5xl font-black mt-3">Four simple steps to <span className="gold-grad">wealth on autopilot</span></h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="glass rounded-2xl p-6"
                data-testid={`step-${i}`}
              >
                <div className="w-11 h-11 rounded-lg bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center mb-4">
                  <s.icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div className="text-xs text-white/40 mb-1">0{i + 1}</div>
                <div className="font-semibold text-lg mb-2">{s.title}</div>
                <p className="text-sm text-white/60 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI VISUAL FEATURE */}
      <section className="relative max-w-7xl mx-auto px-6 py-24 overflow-hidden" data-testid="ai-section">
        <div className="absolute inset-0 -z-10">
          <img src={FARM_BG} alt="Growth and farming" className="w-full h-full object-cover opacity-30 brightness-90" />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="label-eyebrow">Powered by AI</div>
            <h2 className="font-display text-4xl lg:text-5xl font-black mt-3 leading-tight">An autonomous engine <span className="gold-grad">that never sleeps</span>.</h2>
            <p className="text-white/70 mt-6 leading-relaxed">Our proprietary AI scans hundreds of markets every second, identifies edges across spot and futures, and executes risk-controlled trades on your behalf. You get the returns. The engine does the work.</p>
            <ul className="mt-7 space-y-3">
              {[
                ["24/7 market coverage across BTC, ETH, alts & equities", Zap],
                ["Drawdown caps & dynamic position sizing per trade", ShieldCheck],
                ["Real-time portfolio growth visualization in your dashboard", Activity],
                ["Joint accounts share a single AI strategy for synchronized growth", Heart],
              ].map(([t, Icon], i) => (
                <li key={i} className="flex gap-3 text-sm text-white/85">
                  <Icon className="w-5 h-5 text-[#00D68F] flex-shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="relative">
            <div className="absolute inset-0 rounded-3xl tier-glow-elite"></div>
            <img src={AI_IMG} alt="AI trading" className="relative rounded-3xl border border-white/10 w-full" />
          </motion.div>
        </div>
      </section>

      {/* JOINT ACCOUNT */}
      <section className="relative bg-black/30 py-24 overflow-hidden" data-testid="joint-section">
        <div className="absolute inset-0 -z-10">
          <img src={MONEY_BG} alt="Counting money" className="w-full h-full object-cover opacity-30 brightness-90" />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="order-2 lg:order-1">
            <img src={COUPLE_IMG} alt="Couple investing" className="rounded-3xl border border-white/10 w-full" />
          </motion.div>
          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 glass px-3 py-1.5 rounded-full mb-4">
              <Heart className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span className="text-xs uppercase tracking-widest text-white/80">Joint Accounts</span>
            </div>
            <h2 className="font-display text-4xl lg:text-5xl font-black leading-tight">Build wealth <span className="gold-grad">side by side</span>.</h2>
            <p className="text-white/70 mt-6 leading-relaxed">Duocapital is the only AI investment platform built for couples and business partners. Open a shared Joint Account, both log in independently, and watch the same portfolio grow â€” together.</p>
            <ul className="mt-7 space-y-3 text-sm text-white/85">
              <li className="flex gap-2"><Star className="w-4 h-4 text-[#D4AF37] mt-0.5" /> Unique Joint Account ID shared between both partners</li>
              <li className="flex gap-2"><Star className="w-4 h-4 text-[#D4AF37] mt-0.5" /> Live "Joint Account" badge on dashboard</li>
              <li className="flex gap-2"><Star className="w-4 h-4 text-[#D4AF37] mt-0.5" /> Synchronized balance, investments & withdrawals</li>
            </ul>
            <Link to="/register?type=joint" className="btn-primary inline-flex mt-8 items-center gap-2" data-testid="joint-cta">Open a Joint Account <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="testimonials">
        <div className="text-center mb-14">
          <div className="label-eyebrow">Investor Stories</div>
          <h2 className="font-display text-4xl lg:text-5xl font-black mt-3">Trusted by thousands <span className="gold-grad">worldwide</span></h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="glass rounded-2xl p-7"
              data-testid={`testimonial-${i}`}
            >
              <div className="flex gap-0.5 mb-4">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />)}
              </div>
              <p className="text-sm text-white/85 leading-relaxed">"{t.text}"</p>
              <div className="flex items-center gap-3 mt-6">
                <img src={t.img} alt={t.name} className="w-11 h-11 rounded-full object-cover border border-white/10" />
                <div>
                  <div className="text-sm font-semibold">{t.name}</div>
                  <div className="text-xs text-white/50">{t.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="max-w-7xl mx-auto px-6 pb-10" data-testid="trust-bar">
        <div className="glass-strong rounded-2xl p-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            [ShieldCheck, "SSL Secured", "Bank-grade encryption"],
            [Lock, "256-bit Encryption", "Enterprise security"],
            [Activity, "24/7 Monitoring", "Real-time threat detection"],
            [Globe, "Global Coverage", "Available in 180+ countries"],
          ].map(([I, t, d], i) => (
            <div key={i} className="flex items-center gap-3">
              <I className="w-7 h-7 text-[#D4AF37] flex-shrink-0" />
              <div>
                <div className="text-sm font-semibold">{t}</div>
                <div className="text-xs text-white/50">{d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <FAQ />

      <Footer />
    </div>
  );
}
